import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import type {TabItems, TabKey} from '@Components/tabs';
import {Tabs, useSelectedTab} from '@Components/tabs';
import {
  AI_IMAGE_LOADING_SKELETONS,
  AudioFilters,
  CarouselData,
  DEFAULT_APPLIED_SUBFILTER, getHalloweenFilter,
  getStockTabsToFilterMapping,
  MIN_STOCK_ITEMS_TO_DISPLAY,
  SelectedFilter,
  STOCK_PANEL_TAB_ID,
  STOCK_SUBFILTER_TO_ADDITIONAL_FILTERS_MAP,
  STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING,
  StockButtonFilters,
  StockCarouselIDs,
  StockCarouselType,
  StockFilter,
  StockFilterID,
  StockPanelTabs,
  StockPrimaryCTA,
} from '@Panels/stock-panel/stock-panel.types';
import {Text, TextSize, TextType} from '@Components/text';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {getRandomNumberInRange} from '@Utils/math.util';
import type {
  AITextToImageBackendData,
  AudioCategoryBackendData,
  GenerateAIImageResponse,
  GenerateImageParams,
  StabilityAIStylePreset,
  StockResponse,
} from '@Libraries/pmw-stock-media-library';
import {
  AspectRatio,
  GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE,
  GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE,
  GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE,
  generateImage,
  getGeneratedImagesForCarousel,
  getRecentStockItems,
  getRemainingTextToImageUsages,
  getStockForCarousel,
  getStockLibraryAudioCategories,
  setRecentStockItem,
  StockSources,
} from '@Libraries/pmw-stock-media-library';
import Carousel from '@Components/carousel/carousel';
import type {
  PMWStockBackendAnimatedSticker,
  PMWStockBackendExtractedGettySticker,
  PMWStockBackendIcon,
  PMWStockBackendShape,
  PMWStockBackendStillSticker,
} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import {
  createKeepFiltersHiddenForTab,
  FALLBACK_GRID_ITEM_HEIGHT,
  FALLBACK_GRID_ITEM_WIDTH,
  getBottomPills,
  getCarouselDataForAudioItem,
  getCarouselDataForExtractedGettyStickerItem,
  getCarouselDataForIconItem,
  getCarouselDataForImageItem,
  getCarouselDataForShapeItem,
  getCarouselDataForStickerItem,
  getCarouselDataForStillStickerItem,
  getCarouselDataForVideoItem,
  getCostInCreditsForModel,
  getDefaultSelectedFilterForTab,
  getGATrackingFilterTerm,
  getGATrackingSearchTerm,
  getGATrackingSourceFromMediaSource,
  getGATrackingTypeFromMediaType,
  getGridID,
  getGridItemForAIGeneratedImageV2,
  getGridItemForBackendData,
  getHasUserSeenMobileVideoPreview,
  getHeightForCarouselType,
  getInitialOffsetsForInitialCarouselRequest,
  getOptionsForStockGetter,
  getPanelTitle,
  getPlaceholderTextForAiImagePrompt,
  getRequiredSourcesForSelectedTab,
  getSearchBarPlaceholderText,
  getStockFilterForAudioFilterBackendData,
  getStockTabsOrder,
  getTabItem,
  GRID_SKELETON_LANDSCAPE_WIDTH,
  GRID_SKELETON_PORTRAIT_WIDTH,
  GRID_SKELETON_SQUARE_WIDTH,
  markUserHasSeenMobileVideoPreview,
  SHOULD_DISPLAY_AUDIO_ITEMS_IN_ALL_TAB_FOR_MOBILE,
  STOCK_AUDIO_GRID_ITEM_WIDTH,
  uploadSelectedItem,
} from '@Panels/stock-panel/stock-panel.library';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import StockPanelError from '@Panels/stock-panel/components/stock-panel-error';
import type {BasePanel, PanelGrowlData} from '@Components/panel/panel.types';
import {PanelDataKeys} from '@Components/panel/panel.types';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import StockPanelFooter from '@Panels/stock-panel/components/stock-panel-footer';
import {SOURCE} from '@Libraries/stock-graphic-library';
import {validateGettyLicenseAgreement} from '@Panels/premium-agreement-panel/premium-agreement.library';
import {hideLoading, showLoading, updateLoadingText} from '@Libraries/loading-toast-library';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import type {ElementData} from '@Libraries/add-media-library';
import StockPanelSearchBar from '@Panels/stock-panel/components/stock-panel-search-bar';
import StockPanelGrid from '@Panels/stock-panel/components/stock-panel-grid';
import StockFilters from '@Panels/stock-panel/components/stock-filters';
import {setRecentStockAvailability, setStockGetterOptions, toggleFiltersVisibility} from '@Panels/stock-panel/stock-panel-reducer';
import {addItemsToGrid, clearSelectedItems, deleteGridItems, getSelectedGridItems, reloadGrid, replaceGridItem, toggleGridItemSelection} from '@Components/base-grid';
import type {PanelBottomPill} from '@Components/panel/components/panel-bottom-pills';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {deleteTabStateForId, selectTab} from '@Components/tabs/tabs-reducer';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {CarouselItemData} from '@Components/carousel/carousel.types';
import type {InputFieldPropsWithRef} from '@Panels/stock-panel/components/ai-image-prompt';
import {AiImagePrompt} from '@Panels/stock-panel/components/ai-image-prompt';
import {InputFieldSize, InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {TextToImageGrid} from '@Components/text-to-image-grid';
import type {GridStockImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import type {IconProps} from '@Components/icon-v2';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Size, Type} from '@Components/button';
import type {GridGenericButtonItemStorage} from '@Components/base-grid/components/grid-generic-button-item';
import {showTextToImageUpsellDialog} from '@Libraries/text-to-image-library';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {TextToImageGridErrorState} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {updateAiImageState, updateIsUploadingToMyUploads} from '@Components/poster-editor/poster-editor-reducer';
import {AiImageState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {formatNumber, formatToThousand} from '@Utils/number.util';
import {AnimatePresence, motion} from 'framer-motion';
import type {Dimensions} from '@floating-ui/react';
import {ElementTestId} from '@Tests/tests.types';
import {pauseAllAudioGridItems} from '@Components/base-grid/components/grid-user-audio-item/grid-audio-item-reducer';
import {isMobile} from 'react-device-detect';
import useWindowSize from '@Hooks/useWindowSize';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './stock-panel.module.scss';
import {updatePanelData} from '@Components/panel/panel-reducer';
import {AiImageModelOption} from '@Panels/ai-image-panel/ai-image-panel.types';
import {AI_IMAGE_PROMPT_MAX_LENGTH} from '@Panels/ai-image-panel/ai-image-panel.types';
import {updateSelectedAiImageModelId, updateSelectedAiImageModelIdBackend} from '@/reducers/user-preferences.reducer';

const MORE_OPTIONS_TABS_THRESHOLD = 1050;

const MOBILE_VIDEO_PREVIEW_GROWL_VISIBLE_DURATION = 3000;

export interface StockPanelProps extends BasePanel {
  preSelectedFilters?: SelectedFilter[];
  keepFiltersHiddenForTab?: Record<StockPanelTabs, boolean>;
  bottomPills?: PanelBottomPill[];
  tabsToShow?: StockPanelTabs[];
  defaultSearchTab?: StockPanelTabs;
  maxSelectionNumber?: number;
  selectionID?: string;
  addInBulk?: boolean;
  shouldLoadDataUrlImage?: boolean;
  showTextToImageTool?: boolean;
  populateRecentlyAddedOnItemAdd?: boolean;
  videoQualityFiltersDisabled?: boolean;
  tabId?: string;
  stockTabToGridIdMapping?: Record<StockPanelTabs, string>;
  onStartAddSelected?: () => void;
  isManageappPage?: boolean;
  blacklistedSources?: StockSources[];
  bypassCompoundedView?: boolean;

  onAddSelected(items: ElementData[], currentUploadIndex?: number, totalUploadsCount?: number): void | Promise<void>;

  onBack?(): void;

  onClose?(): void;
}

const [MIN_SKELETON_WIDTH, MAX_SKELETON_WIDTH] = [80, 220];
const TOTAL_SKELETONS_PER_ROW = 10;
const DEFAULT_SELECTION_ID = 'STOCK_MEDIA_MODAL_SELECTION_ID';
const query: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 640,
  },
  large: {
    minWidth: 641,
  },
};
const MIN_NUMBER_OF_BOTTOM_PILLS_TO_SHOW = 2;
const CAROUSEL_BUTTON_ITEM_WIDTH = 264;

const AI_ICON_PROPS: IconProps = {
  icon: 'icon-ai-image',
  size: IconSize.SIZE_ICON_20,
  className: `${styles.aiIconStyle} spacing-m-r-1`,
};

export default function StockPanel({
  populateRecentlyAddedOnItemAdd = true,
  showTextToImageTool = false,
  tabsToShow = getStockTabsOrder(!!showTextToImageTool),
  videoQualityFiltersDisabled = false,
  onStartAddSelected = (): void => {},
  tabId = STOCK_PANEL_TAB_ID,
  preSelectedFilters = [],
  keepFiltersHiddenForTab = createKeepFiltersHiddenForTab(),
  isManageappPage = false,
  bypassCompoundedView = false,

  ...props
}: StockPanelProps): ReactElement<any> {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(bypassCompoundedView || tabsToShow.length === 1 ? '' : undefined);
  const panelId = props.panelId ?? 'STOCK_MEDIA_PANEL_NO_ID_PASSED';
  const selectedTab = useSelectedTab(tabId, props.defaultSearchTab ?? tabsToShow[0]) as StockPanelTabs;
  const stockTabsToFilterMapping = useRef<Record<StockPanelTabs, StockFilter[]>>(getStockTabsToFilterMapping(videoQualityFiltersDisabled));
  const aiImagePromptPlaceholderText = useRef<string>(getPlaceholderTextForAiImagePrompt());
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>(preSelectedFilters);
  const [loaded, setLoaded] = useState(false);
  const [recentStockLoaded, setRecentStockLoaded] = useState(false);
  const [generatedImagesLoaded, setGeneratedImagesLoaded] = useState(false);
  const [errorState, setErrorState] = useState<TextToImageGridErrorState>(TextToImageGridErrorState.NONE);
  const [retryCarousels, setRetryCarousels] = useState(false);
  const [renderFilterSubFilters, setRenderFilterSubFilters] = useState<string | undefined>(undefined);
  const carouselData = useRef<CarouselData>({});
  const panelContentRef = useRef<HTMLDivElement>(null);
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const stockSubfilterToAdditionalFiltersMapping = useRef(STOCK_SUBFILTER_TO_ADDITIONAL_FILTERS_MAP);
  const audioFilterIDs = useRef<(StockButtonFilters | string)[]>(AudioFilters);
  const [selectedAIImageAspectRatio, setSelectedAIImageAspectRatio] = useState<AspectRatio | undefined>(undefined);
  const [selectedAIImageStyle, setSelectedAIImageStyle] = useState<StabilityAIStylePreset | undefined>(undefined);
  const [remainingTextToImageUsages, setRemainingTextToImageUsages] = useState<number | undefined>(undefined);
  const lastSearchTerm = useRef<string | undefined>(undefined);

  const clearSelection = (): void => {
    dispatch(
      clearSelectedItems({
        gridSelectionGroupId: carouselItemsSelectionID.current,
      })
    );
  };
  const onHide = (): void => {
    clearSelection();
    dispatch(pauseAllAudioGridItems());
  };
  const onClose = props.onClose?.bind(null) ?? useDefaultPanelClose(panelId, props.hideOnClose, onHide);
  const carouselItemsSelectionID = useRef(props.selectionID ?? DEFAULT_SELECTION_ID);
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const areFiltersVisible = useAppSelector((state) => {
    return state.stockPanel.areFiltersVisible;
  });
  const isRecentStockAvailble = useAppSelector((state) => {
    return state.stockPanel.isRecentStockAvailable;
  });
  const aiImageState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiImage;
  });
  const params = useContainerQuery(panelContentRef, query);
  const dispatch = useAppDispatch();
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, carouselItemsSelectionID.current);
  });
  const AI_IMAGE_BUTTON_GRID_ITEM: GridGenericButtonItemStorage = {
    type: GRID_ITEM_TYPE.GENERIC_BUTTON,
    id: StockCarouselType.TEXT_TO_IMAGE,
    buttonProps: {
      text: window.i18next.t('pmwjs_generate_your_own'),
      customClasses: `hover-transition-all ${styles.aiCarouselButtonItem}`,
      type: Type.PRIMARY,
      icon: 'icon-ai-image',
      size: Size.XSMALL,
    },
    containerClassName: `${styles.aiCarouselButtonItemContainer} radius-12`,
  };
  const {windowWidth} = useWindowSize();
  const selectedAiModelId = useAppSelector((state) => {
    return state.userPreferences.selectedAiImageModelId;
  });

  const doesFiltersVisibilityNeedToggling = (tabKey: TabKey): boolean => {
    const filtersForTab: StockFilter[] = stockTabsToFilterMapping.current[tabKey as StockPanelTabs];
    return (filtersForTab.length <= 0 && areFiltersVisible) || (filtersForTab.length > 0 && !areFiltersVisible);
  };

  const toggleFiltersVisibilityIfChanged = (tabKey: TabKey): void => {
    if (doesFiltersVisibilityNeedToggling(tabKey)) {
      toggleFilters();
    }
  };

  const onAIImageCarouselButtonClicked = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_AI_IMAGE_GENERATE_YOUR_OWN);
    setSearchTerm('');
    onTabClicked(StockPanelTabs.TEXT_TO_IMAGE);
  };

  const AI_IMAGE_BUTTON_CAROUSEL_ITEM: CarouselItemData = {
    gridItemData: {...AI_IMAGE_BUTTON_GRID_ITEM, onClicked: onAIImageCarouselButtonClicked},
    width: CAROUSEL_BUTTON_ITEM_WIDTH,
    height: getHeightForCarouselType(StockCarouselType.TEXT_TO_IMAGE, params.small),
  };

  const shouldShowBackButton = (): boolean => {
    return (isGridView() && tabsToShow.length > 1) || !!props.onBack;
  };

  const onBackButton = (): void => {
    if ((!isGridView() || tabsToShow.length === 1) && !!props.onBack) {
      props.onBack();
    } else {
      dispatch(selectTab(tabId, props.defaultSearchTab ?? tabsToShow[0]));
      setSelectedFilters([]);
      setSearchTerm(undefined);
    }
  };

  const getHeaderLeftIconProps = (): IconProps | undefined => {
    if (selectedTab === StockPanelTabs.TEXT_TO_IMAGE && tabsToShow.length > 1) {
      return AI_ICON_PROPS;
    }
    return undefined;
  };

  const getHeader = (): ReactElement<any> => {
    return (
      <PanelHeader
        title={getPanelTitle(selectedTab, searchTerm)}
        titleClasses={selectedTab === StockPanelTabs.TEXT_TO_IMAGE ? styles.aiImageTitleText : undefined}
        centerAlignTitle={params.small}
        onClose={onClose}
        showCloseButton
        showBackButton={shouldShowBackButton()}
        onBackButton={onBackButton}
        leftIconProps={getHeaderLeftIconProps()}
      />
    );
  };

  const onCTA: StockPrimaryCTA = async (items): Promise<void> => {
    onClose();
    onStartAddSelected();

    const isAnySourceGetty = items.some((item) => {
      return item.source === SOURCE.GETTY;
    });

    let gettyAgreement = false;

    if (isAnySourceGetty) {
      gettyAgreement = await validateGettyLicenseAgreement();
    }

    showLoading('addStockMediaItem', {
      text: `${window.i18next.t('pmwjs_adding')} 1 of ${items.length}`,
    });

    const allItems = [];
    let recentStockItemCount = 0;

    for (let i = 0; i < items.length; i++) {
      if (items[i].source === SOURCE.GETTY && !gettyAgreement) {
        continue;
      }

      updateLoadingText('addStockMediaItem', `${window.i18next.t('pmwjs_adding')} ${i + 1} of ${items.length}`);
      trackPosterBuilderGA4Events(`MediaModal_${getGATrackingSourceFromMediaSource(items[i].source as SOURCE) + getGATrackingTypeFromMediaType(items[i].type)}Add`);

      try {
        if (items[i].source === SOURCE.AI_TEXT_TO_IMAGE) {
          dispatch(updateIsUploadingToMyUploads(true));
        }

        const onAddItemResponse = await uploadSelectedItem(items[i], props.shouldLoadDataUrlImage);

        dispatch(updateIsUploadingToMyUploads(false));

        if (!props.addInBulk) {
          await props.onAddSelected([onAddItemResponse], i + 1, items.length);
        } else {
          allItems.push(onAddItemResponse);
        }

        if (populateRecentlyAddedOnItemAdd && isUserLoggedIn.current) {
          recentStockItemCount = await setRecentStockItem(items[i]);
        }
      } catch (e) {
        console.error(e);

        openModal(MODAL_IDS.ERROR_MODAL_ID, {
          message: window.i18next.t('pmwjs_general_ajax_error'),
          log: false,
        });
      }
    }

    if (props.addInBulk) {
      await props.onAddSelected(allItems);
    }

    dispatch(setRecentStockAvailability(recentStockItemCount >= MIN_STOCK_ITEMS_TO_DISPLAY));
    hideLoading('addStockMediaItem');
  };

  // CodeReviewUsama: Have a separate state for identifying whether it's the carousels view or not rather than using searchTerm undefined for it.
  const shouldShowBottomPills = (bottomPillsLength: number): boolean => {
    return (isGridView() && params.small && bottomPillsLength >= MIN_NUMBER_OF_BOTTOM_PILLS_TO_SHOW) || !!props.bottomPills;
  };

  const getTooltipContent = (): ReactElement<any> => {
    return (
      <div className="flex-center flex-v-row">
        <Text type={TextType.BODY} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_curently_credit_credits', {credit: formatNumber(remainingTextToImageUsages ?? 0)})} />
        <Text
          dangerouslySetInnerHTML
          type={TextType.BODY}
          size={TextSize.XSMALL}
          val={
            selectedAiModelId
              ? window.i18next.t('pmwjs_will_cost_x_credit', {
                  creditsCost: getCostInCreditsForModel(selectedAiModelId),
                  pluralSuffix: getCostInCreditsForModel(selectedAiModelId) > 1 ? 's' : '',
                })
              : window.i18next.t('pmwjs_will_cost_1_credit')
          }
        />
      </div>
    );
  };

  const getFooter = (): ReactElement<any> => {
    const bottomPills = getPanelBottomPills();

    return (
      <StockPanelFooter
        isMobile={params.small}
        onPrimaryAction={onCTA}
        selectionGroupID={carouselItemsSelectionID.current}
        bottomPillItems={shouldShowBottomPills(bottomPills.length) ? bottomPills : undefined}
        makeBottomPillsScrollable={!props.bottomPills}
        aiImageCTAProps={{
          pillToolTipContent: getTooltipContent(),
          isLoadingButtonOverlayShown: true,
          icon: errorState !== TextToImageGridErrorState.NONE ? 'icon-refresh' : 'icon-ai-image',
          topPillContent: formatToThousand(remainingTextToImageUsages ?? 0),
          size: Size.MEDIUM,
          disabled: isAiImageCTADisabled(),
          text: getGenerationButtonText(),
          onClick: onAiButtonCtaClicked,
          isLoading: aiImageState === AiImageState.GENERATING,
          variant: !isPremium.current ? AIButtonVariant.PREMIUM : undefined,
          pillClassName: !isPremium.current ? `${styles.premiumPill} spacing-p-l-0 spacing-p-r-0` : 'spacing-p-r-1 spacing-p-l-1 ',
        }}
      />
    );
  };

  const onAiButtonCtaClicked = (): void => {
    switch (errorState) {
      case TextToImageGridErrorState.NONE:
      case TextToImageGridErrorState.GENERIC:
        onAIImageGenerate();
        break;
      case TextToImageGridErrorState.FLAGGED:
      case TextToImageGridErrorState.UNSUPPORTED_LANUAGE:
        setSearchTerm('');
        setErrorState(TextToImageGridErrorState.NONE);
        break;
      default:
        throw new Error(`Unhandled error state: ${errorState}`);
    }
  };

  const isAiImageCTADisabled = (): boolean => {
    if (aiImageState === AiImageState.GENERATING) {
      return false;
    }

    return !searchTerm || searchTerm.length === 0;
  };

  const getTabItems = (): TabItems => {
    return tabsToShow.map((tab) => {
      return {
        ...getTabItem(tab, errorState !== TextToImageGridErrorState.NONE, tab === StockPanelTabs.TEXT_TO_IMAGE ? styles.aiImageTitleText : undefined),
        ...(tab === StockPanelTabs.TEXT_TO_IMAGE ? {preTitleContent: <Icon {...AI_ICON_PROPS} />} : {}),
      };
    });
  };

  const getGridIdForTab = (tab: StockPanelTabs): string => {
    if (props.stockTabToGridIdMapping) {
      return props.stockTabToGridIdMapping[tab];
    }
    return getGridID(tab);
  };

  const onTabClicked = (tabKey: TabKey): void => {
    const currentGridID = getGridIdForTab(selectedTab);

    if (selectedTab !== tabKey) {
      dispatch(
        reloadGrid({
          gridId: currentGridID,
          gridSelectionGroupId: carouselItemsSelectionID.current,
        })
      );
      dispatch(selectTab(tabId, tabKey));
      toggleFiltersVisibilityIfChanged(tabKey);
      setSelectedFilters(getDefaultSelectedFilterForTab(tabKey as StockPanelTabs));
      dispatch(setStockGetterOptions({}));
    }
  };

  const shouldShowClearAllFilters = (): boolean => {
    return !areFiltersVisible && selectedFilters.length > 0;
  };

  const showMoreOptionsInTabs = (): boolean => {
    return windowWidth < MORE_OPTIONS_TABS_THRESHOLD;
  };

  const getTabs = (): ReactElement<any> => {
    return (
      <div className={`_full-width flex-justify-between flex-row-align-center spacing-m-t-8 `}>
        <Tabs
          id={tabId}
          moreOptionsIndex={showMoreOptionsInTabs() ? 5 : undefined}
          tabItems={getTabItems()}
          selectedTab={selectedTab}
          onTabClick={onTabClicked}
          className="spacing-m-b-0 spacing-p-0"
        />
        {shouldShowClearAllFilters() ? (
          <Text
            onClick={(): void => {
              setSelectedFilters([]);
            }}
            className={`${styles.clearAllFiltersText} content-link`}
            val={window.i18next.t('pmwjs_clear_filters')}
            size={TextSize.XSMALL}
            underline
          />
        ) : null}
      </div>
    );
  };

  const toggleFilters = (): void => {
    dispatch(toggleFiltersVisibility());
    if (renderFilterSubFilters) {
      setRenderFilterSubFilters(undefined);
    }
  };

  const getAdditionalFilters = (tab: StockPanelTabs, filterID?: string, subfilterID?: string): StockFilter[] => {
    if (!subfilterID || !filterID) {
      return [];
    }
    if (stockSubfilterToAdditionalFiltersMapping.current[tab][filterID]) {
      return stockSubfilterToAdditionalFiltersMapping.current[tab][filterID][subfilterID] ?? [];
    }
    return [];
  };

  const onGeneratedImagesReceived = (response: AITextToImageBackendData[]): void => {
    const items: GridStockImageItemStorage[] = response.map(getGridItemForAIGeneratedImageV2);
    const toReplace = [...AI_IMAGE_LOADING_SKELETONS];

    items.forEach((gridItem): void => {
      const skeleton = toReplace.pop();
      if (skeleton) {
        dispatch(replaceGridItem({gridId: getGridIdForTab(selectedTab), itemId: skeleton.id, gridItem}));
      }
    });

    if (toReplace.length > 0) {
      dispatch(
        deleteGridItems({
          gridId: getGridIdForTab(selectedTab),
          gridSelectionGroupId: carouselItemsSelectionID.current,
          itemIdsToDelete: toReplace.map((skeleton): string => {
            return skeleton.id;
          }),
        })
      );
    }

    setErrorState(TextToImageGridErrorState.NONE);
  };

  const getDimensionsForAiImageSkeletonItem = (): Partial<GridSkeletonItemStorage> => {
    switch (selectedAIImageAspectRatio) {
      case AspectRatio.PORTRAIT:
        return {width: GRID_SKELETON_PORTRAIT_WIDTH};
      case AspectRatio.LANDSCAPE:
        return {width: GRID_SKELETON_LANDSCAPE_WIDTH};
      case AspectRatio.SQUARE:
      default:
        return {width: GRID_SKELETON_SQUARE_WIDTH};
    }
  };

  const onAIImageGenerateSuccess = (generateImagesResponse: GenerateAIImageResponse): void => {
    onGeneratedImagesReceived(generateImagesResponse.generatedImages);
    setRemainingTextToImageUsages(generateImagesResponse.remainingCredits);
    setErrorState(TextToImageGridErrorState.NONE);
  };

  const addLoadingSkeletons = (): void => {
    dispatch(
      addItemsToGrid({
        id: getGridIdForTab(selectedTab),
        index: 0,
        items: AI_IMAGE_LOADING_SKELETONS.map((skeletonItem): GridItemStorage => {
          return {...skeletonItem, ...getDimensionsForAiImageSkeletonItem()};
        }),
      })
    );
  };

  const removeLoadingSkeletons = (): void => {
    dispatch(
      deleteGridItems({
        gridId: getGridIdForTab(StockPanelTabs.TEXT_TO_IMAGE),
        gridSelectionGroupId: carouselItemsSelectionID.current,
        itemIdsToDelete: AI_IMAGE_LOADING_SKELETONS.map((item) => {
          return item.id;
        }),
      })
    );
  };

  const handleInsufficientCredits = (): void => {
    dispatch(
      deleteGridItems({
        gridId: getGridIdForTab(selectedTab),
        gridSelectionGroupId: carouselItemsSelectionID.current,
        itemIdsToDelete: AI_IMAGE_LOADING_SKELETONS.map((item) => {
          return item.id;
        }),
      })
    );

    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase((data) => {
        if (data.credits > 0) {
          onAIImageGenerate();
        }
      });
    });
  };

  const onAIImageGenerateError = (e: Error): void => {
    setErrorState((): TextToImageGridErrorState => {
      if (e.message === GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE) {
        handleInsufficientCredits();
        return TextToImageGridErrorState.NONE;
      }
      if (e.message === GENERATE_IMAGE_FLAGGED_PROMPT_RESPONSE_CODE) {
        return TextToImageGridErrorState.FLAGGED;
      }
      if (e.message === GENERATE_IMAGE_UNSUPPORTED_LANGUAGE_RESPONSE_CODE) {
        return TextToImageGridErrorState.UNSUPPORTED_LANUAGE;
      }
      return TextToImageGridErrorState.GENERIC;
    });

    removeLoadingSkeletons();
  };

  const onAIImageGenerate = (): void => {
    if (!isPremium.current) {
      void showTextToImageUpsellDialog();
      return;
    }

    if (searchTerm && searchTerm.length > 0) {
      dispatch(updateAiImageState(AiImageState.GENERATING));
      addLoadingSkeletons();
      trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_AI_IMAGE_GENERATE, {
        [GA4EventParamName.TYPE]: selectedAIImageStyle ?? '',
      });
      lastSearchTerm.current = searchTerm;
      const generateImageParams: GenerateImageParams = {
        prompt: searchTerm,
        style: selectedAIImageStyle,
        aspectRatio: selectedAIImageAspectRatio,
        providerId: selectedAiModelId !== AiImageModelOption.Multi ? selectedAiModelId : undefined,
      };
      generateImage(generateImageParams)
        .then(onAIImageGenerateSuccess)
        .catch(onAIImageGenerateError)
        .finally(() => {
          dispatch(updateAiImageState(AiImageState.READY));
          setTimeout(() => {
            dispatch(updateAiImageState(AiImageState.IDLE));
          }, 2000);
        });
    }
  };

  const getGenerationButtonText = (): string => {
    switch (errorState) {
      case TextToImageGridErrorState.NONE:
        return searchTerm === lastSearchTerm.current ? window.i18next.t('pmwjs_generate_more') : window.i18next.t('pmwjs_generate');
      case TextToImageGridErrorState.GENERIC:
      case TextToImageGridErrorState.UNSUPPORTED_LANUAGE:
        return window.i18next.t('pmwjs_retry');
      case TextToImageGridErrorState.FLAGGED:
        return window.i18next.t('pmwjs_try_a_different_term');
      default:
        throw new Error(`Unhandled error state: ${errorState}`);
    }
  };

  const getSearchBarContainer = (): ReactElement<any> => {
    if (isTextToImageTabActive()) {
      const inputFieldProps: InputFieldPropsWithRef = {
        isControlled: true,
        inputType: InputFieldType.SEARCH,
        input: searchTerm,
        onInputChange: (updatedTerm: string): void => {
          setSearchTerm(updatedTerm);
        },
        onInputSubmit() {
          if (searchTerm && searchTerm.trim().length > 0) {
            onAIImageGenerate();
          }
        },
        size: InputFieldSize.MEDIUM,
        isDisabled: aiImageState === AiImageState.GENERATING,
        state: errorState === TextToImageGridErrorState.FLAGGED ? InputFieldState.ERROR : InputFieldState.DEFAULT,
        inputClassName: styles.searchBarHeightAiImage,
        autoFocus: true,
        placeholder: aiImagePromptPlaceholderText.current,
        maxLength: AI_IMAGE_PROMPT_MAX_LENGTH,
      };
      return (
        <AiImagePrompt
          errorState={errorState}
          isMobile={params.small}
          onGenerateButtonClicked={onAiButtonCtaClicked}
          className="_full-width _full-height"
          inputFieldProps={inputFieldProps}
          isGenerating={aiImageState === AiImageState.GENERATING}
          selectedAspectRatio={selectedAIImageAspectRatio!}
          selectedAiImageModel={selectedAiModelId}
          onAiImageModelSelected={onAiImageModelSelected}
          selectedStylePresets={selectedAIImageStyle}
          onStyleChanged={(id) => {
            setSelectedAIImageStyle(id as StabilityAIStylePreset);
          }}
          onAspectRatioChanged={(id) => {
            setSelectedAIImageAspectRatio(id as AspectRatio);
          }}
          remainingUsages={remainingTextToImageUsages}
          generationButtonText={getGenerationButtonText()}
        />
      );
    }
    return (
      <StockPanelSearchBar
        showCrossIcon={typeof searchTerm !== 'undefined'}
        className={params.small ? 'spacing-m-b-2' : ''}
        onSearch={(updatedSearchTerm) => {
          setSearchTerm((prevState) => {
            lastSearchTerm.current = prevState;
            return updatedSearchTerm;
          });
        }}
        placeholderText={getSearchBarPlaceholderText(isGridView() ? selectedTab : undefined)}
        inputFieldStyles={styles.searchBarHeightStandard}
        val={searchTerm}
      />
    );
  };

  const onAiImageModelSelected = (modelId?: AiImageModelOption): void => {
    if (modelId) {
      void updateSelectedAiImageModelIdBackend(modelId);
    }
    dispatch(updateSelectedAiImageModelId(modelId));
  };

  const shouldAddDividersBetweenFilters = (): boolean => {
    return selectedTab !== StockPanelTabs.AUDIOS && selectedTab !== StockPanelTabs.ICONS && !videoQualityFiltersDisabled;
  };

  const getFiltersRow = (): ReactElement<any> => {
    const filtersForTab = stockTabsToFilterMapping.current[selectedTab];
    const additionalFilters = selectedFilters
      .map((selectedFilter) => {
        return getAdditionalFilters(selectedTab, selectedFilter.filter, selectedFilter.subFilter ?? undefined);
      })
      .flat();

    return (
      <StockFilters
        pillSize={params.small ? PillButtonSize.SMALL : PillButtonSize.MEDIUM}
        isVisible={areFiltersVisible && !keepFiltersHiddenForTab[selectedTab]}
        onFilterItemClicked={onFilterChanged}
        selectedFilters={selectedFilters}
        items={[...filtersForTab, ...additionalFilters]}
        className={`spacing-m-b-2 ${params.small ? 'spacing-m-t-2' : 'spacing-m-t-5'}`}
        addDividers={shouldAddDividersBetweenFilters()}
        isMobile={params.small}
      />
    );
  };

  // CodeReviewUsama: Use undfined instead of null. In ts usage of null isn't recommended
  const onFilterChanged = (filterID: StockFilterID, subFilterID: string | null): void => {
    let requiredFilters = selectedFilters.filter((selectedFilter) => {
      return selectedFilter.filter !== filterID.valueOf();
    });

    if (subFilterID !== null && subFilterID !== DEFAULT_APPLIED_SUBFILTER.id) {
      requiredFilters.push({
        filter: filterID,
        subFilter: subFilterID,
      });
    }

    const filterIDsToRemove: StockFilterID[] = Object.keys(stockSubfilterToAdditionalFiltersMapping.current[selectedTab][filterID] ?? [])
      .map((key) => {
        return stockSubfilterToAdditionalFiltersMapping.current[selectedTab][filterID][key].map((stockFilter) => {
          return stockFilter.id;
        });
      })
      .flat();

    requiredFilters = requiredFilters.filter((selectedfilter) => {
      return !filterIDsToRemove.includes(selectedfilter.filter as StockFilterID);
    });

    setSelectedFilters(requiredFilters);
  };

  const onGridItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    if (isDoubleClick && selectedItems.length === 1) {
      void onCTA(selectedItems);
    } else {
      dispatch(
        toggleGridItemSelection({
          gridItemId: item.id,
          gridSelectionGroupId: carouselItemsSelectionID.current,
        })
      );
    }
  };

  const isTextToImageGridInErrorState = (): TextToImageGridErrorState => {
    if (aiImageState === AiImageState.GENERATING) {
      return TextToImageGridErrorState.NONE;
    }
    return errorState;
  };

  const getAIImageGrid = (gridItemID: string): ReactElement<any> => {
    const gridProps: TextToImageGridProps = {
      customOnGridItemClicked: onGridItemClicked,
      gridClasses: `spacing-p-t-6 spacing-p-b-6 spacing-p-r-3 spacing-p-l-3`,
      gridSelectionGroupId: carouselItemsSelectionID.current,
      maxSelectionNumber: props.maxSelectionNumber,
      gridId: gridItemID,
      isLoading: aiImageState === AiImageState.GENERATING,
      errorState: isTextToImageGridInErrorState(),
    };

    return <TextToImageGrid key={gridItemID} {...gridProps} />;
  };

  const getStockGrid = (): ReactElement<any> => {
    let sourcesToGet =
      props.blacklistedSources && props.blacklistedSources.length > 0
        ? getRequiredSourcesForSelectedTab(selectedTab, selectedFilters).filter((source) => {
            return !props.blacklistedSources?.includes(source);
          })
        : getRequiredSourcesForSelectedTab(selectedTab, selectedFilters);
    if (!SHOULD_DISPLAY_AUDIO_ITEMS_IN_ALL_TAB_FOR_MOBILE && selectedTab === StockPanelTabs.ALL && isMobile) {
      sourcesToGet = sourcesToGet.filter((src: StockSources) => {
        return src !== StockSources.STORYBLOCK_AUDIO;
      });
    }
    const gridItemID = getGridIdForTab(selectedTab);

    if (selectedTab === StockPanelTabs.TEXT_TO_IMAGE) {
      return getAIImageGrid(gridItemID);
    }

    if (selectedTab === StockPanelTabs.STICKERS && isManageappPage) {
      sourcesToGet = sourcesToGet.filter((source) => {
        return source !== StockSources.PMW_EXTRACTED_GETTY_STICKER;
      });
    }

    if (selectedTab === StockPanelTabs.PHOTOS && searchTerm?.length === 0) {
      sourcesToGet = sourcesToGet.filter((source) => {
        return source !== StockSources.PMW_STOCK_IMAGE;
      });
    }

    return (
      <StockPanelGrid
        isManageappPage={isManageappPage}
        key={gridItemID}
        onSingleItemDoubleClick={onCTA}
        searchTerm={searchTerm}
        selectionGroupID={carouselItemsSelectionID.current}
        maxSelectionNumber={props.maxSelectionNumber}
        gridID={gridItemID}
        sources={sourcesToGet}
        generateImageCallback={(): void => {
          onTabClicked(StockPanelTabs.TEXT_TO_IMAGE);
        }}
        gridItemDimensions={
          selectedTab === StockPanelTabs.AUDIOS
            ? ({
                width: 250,
                height: 80,
              } as Dimensions)
            : undefined
        }
      />
    );
  };

  const getLoadingSkeletonRow = (carouselType: StockCarouselType): ReactElement<any>[] => {
    const skeletons: ReactElement<any>[] = [];
    for (let i = 0; i < TOTAL_SKELETONS_PER_ROW; i++) {
      const randomWidth = getRandomNumberInRange(MIN_SKELETON_WIDTH, MAX_SKELETON_WIDTH);
      skeletons.push(
        <li key={i}>
          <SkeletonBrick className="radius-12" height={getHeightForCarouselType(carouselType, params.small)} width={randomWidth} />
        </li>
      );
    }

    return skeletons;
  };

  const getLoadingSkeletonForCarousel = (type: StockCarouselType): ReactElement<any> => {
    return <ul className={`${styles.skeletonList} spacing-m-0 flex-h-row spacing-p-t-4`}>{getLoadingSkeletonRow(type)}</ul>;
  };

  const onViewAllClicked = (carouselType: StockCarouselType): void => {
    switch (carouselType) {
      case StockCarouselType.AUDIOS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.AUDIOS));
        dispatch(selectTab(tabId, StockPanelTabs.AUDIOS));
        break;
      case StockCarouselType.PHOTOS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.PHOTOS));
        dispatch(selectTab(tabId, StockPanelTabs.PHOTOS));
        break;
      case StockCarouselType.SHAPES:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.SHAPES));
        dispatch(selectTab(tabId, StockPanelTabs.SHAPES));
        break;
      case StockCarouselType.STICKERS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.STICKERS));
        dispatch(selectTab(tabId, StockPanelTabs.STICKERS));
        break;
      case StockCarouselType.ICONS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.ICONS));
        dispatch(selectTab(tabId, StockPanelTabs.ICONS));
        break;
      case StockCarouselType.VIDEOS:
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.VIDEOS));
        dispatch(selectTab(tabId, StockPanelTabs.VIDEOS));
        break;
      case StockCarouselType.TEXT_TO_IMAGE:
        trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_AI_IMAGE_VIEW_ALL);
        setSelectedFilters(getDefaultSelectedFilterForTab(StockPanelTabs.TEXT_TO_IMAGE));
        dispatch(selectTab(tabId, StockPanelTabs.TEXT_TO_IMAGE));
        break;
      default:
        throw new Error(`Unhandled carousel type: ${carouselType}`);
    }
    setSearchTerm('');
  };

  const shouldShowShowMoreCTA = (carouselType: StockCarouselType): boolean => {
    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return false;
      case StockCarouselType.TEXT_TO_IMAGE:
      case StockCarouselType.ICONS:
      case StockCarouselType.AUDIOS:
      case StockCarouselType.PHOTOS:
      case StockCarouselType.SHAPES:
      case StockCarouselType.STICKERS:
      case StockCarouselType.VIDEOS:
        return true;
      default:
        throw new Error(`Unhanled carousel type: ${carouselType}`);
    }
  };

  const getTextForCarouselLabel = (carouselType: StockCarouselType): ReactElement<any> => {
    switch (carouselType) {
      case StockCarouselType.STICKERS:
      case StockCarouselType.SHAPES:
      case StockCarouselType.PHOTOS:
      case StockCarouselType.AUDIOS:
      case StockCarouselType.ICONS:
      case StockCarouselType.RECENTS:
      case StockCarouselType.VIDEOS:
        return <Text val={getCarouselTitleText(carouselType)} className="content-body" type={TextType.BODY} size={TextSize.XSMALL} bold />;
      case StockCarouselType.TEXT_TO_IMAGE:
        return (
          <div className={`flex-align-items-center flex-h-row ${styles.textToImageCarouselTitleContainer}`}>
            <Icon icon="icon-ai-image" size={IconSize.SIZE_ICON_16} className={styles.aiIconStyle} />
            <Text val={getCarouselTitleText(carouselType)} className={styles.aiImageTitleText} type={TextType.BODY} size={TextSize.XSMALL} bold />
          </div>
        );
      default:
        throw new Error(`Unhandled carousel type: ${carouselType}`);
    }
  };

  const getCarouselUIForCarouselContent = (carouselContentMarkup: ReactElement<any>, carouselType: StockCarouselType): ReactElement<any> => {
    return (
      <li className={`${styles.carouselRow} spacing-p-0 flex-v-row`} key={carouselType} data-testid={getTestIdForCarouselType(carouselType)}>
        <div className="flex-h-row flex-justify-between">
          {getTextForCarouselLabel(carouselType)}
          {shouldShowShowMoreCTA(carouselType) ? (
            <Text
              className={`${styles.viewAllText} test-view-all`}
              val={window.i18next.t('pmwjs_carousel_view_all')}
              type={TextType.BODY}
              size={TextSize.XSMALL}
              onClick={(): void => {
                onViewAllClicked(carouselType);
              }}
              bold
            />
          ) : null}
        </div>
        {carouselContentMarkup}
      </li>
    );
  };

  const getTestIdForCarouselType = (carouselType: StockCarouselType): ElementTestId | undefined => {
    switch (carouselType) {
      case StockCarouselType.SHAPES:
        return ElementTestId.STOCK_PANEL_CAROUSEL_SHAPES;
      default:
        return undefined;
    }
  };

  const getCarouselTitleText = (carouselType: StockCarouselType): string => {
    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return window.i18next.t('pmwjs_recent');
      case StockCarouselType.PHOTOS:
        return window.i18next.t('pmwjs_photos_2');
      case StockCarouselType.STICKERS:
        return window.i18next.t('pmwjs_stickers');
      case StockCarouselType.SHAPES:
        return window.i18next.t('pmwjs_shapes');
      case StockCarouselType.VIDEOS:
        return window.i18next.t('pmwjs_videos_2');
      case StockCarouselType.AUDIOS:
        return window.i18next.t('pmwjs_audio');
      case StockCarouselType.ICONS:
        return window.i18next.t('pmwjs_icons');
      case StockCarouselType.TEXT_TO_IMAGE:
        return window.i18next.t('pmwjs_ai_images');
      default:
        throw new Error(`Unhandled carousel type for getCarouselTitleText: ${carouselType}`);
    }
  };

  const shouldShowLoadingCarouselForCarouselType = (carouselType: StockCarouselType): boolean => {
    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return !recentStockLoaded;
      case StockCarouselType.VIDEOS:
      case StockCarouselType.STICKERS:
      case StockCarouselType.SHAPES:
      case StockCarouselType.PHOTOS:
      case StockCarouselType.AUDIOS:
      case StockCarouselType.ICONS:
        return !loaded;
      case StockCarouselType.TEXT_TO_IMAGE:
        return !generatedImagesLoaded;
      default:
        throw new Error(`Unhandled carousel type: ${carouselType}`);
    }
  };

  const getCarouselViewRow = (carouselType?: StockCarouselType): ReactElement<any> | null => {
    if (carouselType) {
      if (shouldShowLoadingCarouselForCarouselType(carouselType)) {
        return getCarouselUIForCarouselContent(getLoadingSkeletonForCarousel(carouselType), carouselType);
      }
      const carousel = getCarouselForType(carouselType);
      return carousel ? getCarouselUIForCarouselContent(carousel, carouselType) : null;
    }
    return null;
  };

  const getCarouselForType = (carouselType: StockCarouselType): ReactElement<any> | null => {
    const commonProps = {
      selectionGroupId: carouselItemsSelectionID.current,
      height: getHeightForCarouselType(carouselType, params.small),
      nextBtnRightPositioning: params.small ? -8 : undefined,
      prevBtnLeftPositioning: params.small ? -8 : undefined,
      maxSelectionNumber: props.maxSelectionNumber,
    };

    switch (carouselType) {
      case StockCarouselType.RECENTS:
        return carouselData.current[StockCarouselType.RECENTS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.RECENTS} items={carouselData.current[StockCarouselType.RECENTS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.PHOTOS:
        return carouselData.current[StockCarouselType.PHOTOS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.PHOTOS} items={carouselData.current[StockCarouselType.PHOTOS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.STICKERS:
        return carouselData.current[StockCarouselType.STICKERS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.STICKERS} items={carouselData.current[StockCarouselType.STICKERS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.SHAPES:
        return carouselData.current[StockCarouselType.SHAPES] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.SHAPES} items={carouselData.current[StockCarouselType.SHAPES]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.ICONS:
        return carouselData.current[StockCarouselType.ICONS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.ICONS} items={carouselData.current[StockCarouselType.ICONS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.VIDEOS:
        return carouselData.current[StockCarouselType.VIDEOS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.VIDEOS} items={carouselData.current[StockCarouselType.VIDEOS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.AUDIOS:
        return carouselData.current[StockCarouselType.AUDIOS] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.AUDIOS} items={carouselData.current[StockCarouselType.AUDIOS]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      case StockCarouselType.TEXT_TO_IMAGE:
        return carouselData.current[StockCarouselType.TEXT_TO_IMAGE] ? (
          <Carousel {...commonProps} gridID={StockCarouselIDs.TEXT_TO_IMAGE} items={carouselData.current[StockCarouselType.TEXT_TO_IMAGE]} onSingleItemDoubleClick={onCTA} />
        ) : null;
      default:
        throw new Error(`Unhandled StockCarouselType: ${carouselType}`);
    }
  };

  const getCarousels = (): ReactElement<any> => {
    return errorState ? (
      <StockPanelError
        className="_full-height _full-width"
        text={window.i18next.t('pmwjs_refresh_on_error')}
        onRefresh={(): void => {
          setRetryCarousels(!retryCarousels);
        }}
      />
    ) : (
      <ul className={`${styles.carouselList} spacing-m-t-6 flex-v-row`}>{getCarouselsForTabs()}</ul>
    );
  };

  const getCarouselsForTabs = (): (ReactElement<any> | null)[] => {
    const carouselRow: (ReactElement<any> | null)[] = [];
    tabsToShow.forEach((tab) => {
      let carouselsToShowForTab = STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING[tab];
      if (!isRecentStockAvailble) {
        carouselsToShowForTab = carouselsToShowForTab?.filter((carouselType) => {
          return carouselType !== StockCarouselType.RECENTS;
        });
      }
      const carousels = carouselsToShowForTab?.map(getCarouselViewRow);
      carousels?.forEach((carouselMarkdown) => {
        carouselRow.push(carouselMarkdown);
      });
    });
    return carouselRow;
  };

  const isGridView = (): boolean => {
    return typeof searchTerm !== 'undefined';
  };

  const shouldShowTabs = (): boolean => {
    return isGridView() && tabsToShow.length > 1 && params.large;
  };

  const getPanelBottomPills = (): PanelBottomPill[] => {
    if (props.bottomPills) {
      return props.bottomPills;
    }

    return getBottomPills(selectedTab, onTabClicked).filter((bottomPill) => {
      return tabsToShow.includes(bottomPill.id as StockPanelTabs);
    });
  };

  const getContent = (): ReactElement<any> => {
    return (
      <PanelContent
        ref={panelContentRef}
        className={`flexbox flex-v-row ${styles.panel} ${
          params.small
            ? `spacing-p-4 ${typeof searchTerm !== 'undefined' ? 'spacing-p-b-0' : ''}`
            : `spacing-p-t-8 spacing-p-r-12 spacing-p-l-12 ${typeof searchTerm !== 'undefined' ? 'spacing-p-b-0' : 'spacing-p-b-12'}`
        }`}
      >
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={isTextToImageTabActive() ? 'text-to-image-animation-container' : 'standard-animation-container'}
            initial={{height: 0, opacity: 0}}
            animate={{height: 'auto', opacity: 1}}
            transition={{duration: 0.3}}
            exit={{height: 'auto', opacity: 0}}
          >
            {getSearchBarContainer()}
          </motion.div>
        </AnimatePresence>
        {shouldShowTabs() ? getTabs() : null}
        {isGridView() ? getFiltersRow() : null}
        {getMainScreen()}
      </PanelContent>
    );
  };

  const isTextToImageTabActive = (): boolean => {
    return selectedTab === StockPanelTabs.TEXT_TO_IMAGE && typeof searchTerm !== 'undefined';
  };

  const getMainScreen = (): ReactElement<any> | null => {
    return isGridView() ? getStockGrid() : getCarousels();
  };

  const onCarouselStockReceived = (response: StockResponse): void => {
    setErrorState(TextToImageGridErrorState.NONE);
    const recentStockItemsData = carouselData.current[StockCarouselType.RECENTS] ? [...carouselData.current[StockCarouselType.RECENTS]] : undefined;
    const generatedImagesData = carouselData.current[StockCarouselType.TEXT_TO_IMAGE] ? [...carouselData.current[StockCarouselType.TEXT_TO_IMAGE]] : undefined;
    carouselData.current = {};
    carouselData.current[StockCarouselType.RECENTS] = recentStockItemsData;
    carouselData.current[StockCarouselType.TEXT_TO_IMAGE] = generatedImagesData;
    if (carouselData && carouselData.current) {
      response.stock.forEach((stockItem) => {
        switch (stockItem.type as StockSources) {
          case StockSources.PMW_ANIMATED_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForStickerItem(stockItem as PMWStockBackendAnimatedSticker),
            ];
            break;
          case StockSources.PMW_ICON:
            carouselData.current[StockCarouselType.ICONS] = [
              ...(carouselData.current[StockCarouselType.ICONS] ?? []),
              getCarouselDataForIconItem(stockItem as PMWStockBackendIcon),
            ];
            break;
          case StockSources.PMW_EXTRACTED_GETTY_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForExtractedGettyStickerItem(stockItem as PMWStockBackendExtractedGettySticker),
            ];
            break;
          case StockSources.PMW_STILL_STICKER:
            carouselData.current[StockCarouselType.STICKERS] = [
              ...(carouselData.current[StockCarouselType.STICKERS] ?? []),
              getCarouselDataForStillStickerItem(stockItem as PMWStockBackendStillSticker),
            ];
            break;
          case StockSources.PMW_SHAPE:
            carouselData.current[StockCarouselType.SHAPES] = [
              ...(carouselData.current[StockCarouselType.SHAPES] ?? []),
              getCarouselDataForShapeItem(stockItem as PMWStockBackendShape),
            ];
            break;
          case StockSources.GETTY_IMAGE:
          case StockSources.STORYBLOCK_IMAGE:
          case StockSources.PIXABY_IMAGE:
            carouselData.current[StockCarouselType.PHOTOS] = [
              ...(carouselData.current[StockCarouselType.PHOTOS] ?? []),
              getCarouselDataForImageItem(stockItem, stockItem.type as StockSources),
            ];
            break;
          case StockSources.GETTY_VIDEO:
          case StockSources.STORYBLOCK_VIDEO:
          case StockSources.PIXABY_VIDEO: {
            const carouselDataForVideo = getCarouselDataForVideoItem(stockItem, stockItem.type as StockSources);
            if (carouselDataForVideo) {
              carouselData.current[StockCarouselType.VIDEOS] = [...(carouselData.current[StockCarouselType.VIDEOS] ?? []), carouselDataForVideo];
            }
            break;
          }
          case StockSources.STORYBLOCK_ILLUSTRATION:
          case StockSources.PIXABY_ILLUSTRATION:
          case StockSources.GETTY_ILLUSTRATION: // no need to handle these sources since we don't show these in any Carousel
            break;
          case StockSources.STORYBLOCK_AUDIO:
            carouselData.current[StockCarouselType.AUDIOS] = [
              ...(carouselData.current[StockCarouselType.AUDIOS] ?? []),
              getCarouselDataForAudioItem(stockItem, getHeightForCarouselType(StockCarouselType.AUDIOS, params.small)),
            ];
            break;
          default:
            throw new Error(`Unhandled stock source: ${stockItem.type ?? ''}`);
        }
      });
      setLoaded(true);
    }
  };

  const onAudioCategoriesReceived = (response: AudioCategoryBackendData[]): void => {
    audioFilterIDs.current = [StockButtonFilters.HALLOWEEN];
    const audioFilters = response.map((responseRow) => {
      audioFilterIDs.current.push(responseRow.id);
      return getStockFilterForAudioFilterBackendData(responseRow);
    });
    stockTabsToFilterMapping.current[StockPanelTabs.AUDIOS] = [
      getHalloweenFilter(),
      ...audioFilters,
    ];
  };

  const loadAudioCategories = (): void => {
    getStockLibraryAudioCategories()
      .then(onAudioCategoriesReceived)
      .catch((e) => {
        console.error(e);
      });
  };

  const loadRecentStockItems = (): void => {
    getRecentStockItems()
      .then((res) => {
        carouselData.current[StockCarouselType.RECENTS] =
          res.length >= MIN_STOCK_ITEMS_TO_DISPLAY
            ? res.map((gridItem) => {
                return {
                  gridItemData: getGridItemForBackendData(gridItem),
                  width: gridItem.type === GRID_ITEM_TYPE.STOCK_AUDIO ? STOCK_AUDIO_GRID_ITEM_WIDTH : Number(gridItem.width ?? FALLBACK_GRID_ITEM_WIDTH),
                  height:
                    gridItem.type === GRID_ITEM_TYPE.STOCK_AUDIO
                      ? getHeightForCarouselType(StockCarouselType.RECENTS, params.small)
                      : Number(gridItem.height ?? FALLBACK_GRID_ITEM_HEIGHT),
                } as CarouselItemData;
              })
            : undefined;
        dispatch(setRecentStockAvailability(!!carouselData.current[StockCarouselType.RECENTS]));
        setRecentStockLoaded(true);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const loadGeneratedImages = (): void => {
    getGeneratedImagesForCarousel()
      .then((res) => {
        carouselData.current[StockCarouselType.TEXT_TO_IMAGE] = [
          AI_IMAGE_BUTTON_CAROUSEL_ITEM,
          ...res.map((aiGeneratedImage): CarouselItemData => {
            return {
              gridItemData: getGridItemForAIGeneratedImageV2(aiGeneratedImage),
              width: Number(aiGeneratedImage.imageProps.width),
              height: Number(aiGeneratedImage.imageProps.height),
            };
          }),
        ];
        setGeneratedImagesLoaded(true);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const getRemainingTextToImageInvocations = (): void => {
    getRemainingTextToImageUsages()
      .then((res) => {
        setRemainingTextToImageUsages(res);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const onModalUnmount = (): void => {
    if (areFiltersVisible) {
      dispatch(toggleFiltersVisibility());
    }

    clearSelection();

    dispatch(deleteTabStateForId(tabId));
  };

  const trackMediaItemSearch = (mustHaveSubfilter?: boolean): void => {
    if (!searchTerm) {
      return;
    }
    const filterTerm = selectedTab === StockPanelTabs.STICKERS ? getGATrackingFilterTerm(selectedFilters) : '';
    if (mustHaveSubfilter && !filterTerm) {
      return;
    }
    trackPosterBuilderGA4Events(`MediaModal_${filterTerm + getGATrackingSearchTerm(selectedTab)}Search`, {
      [GA4EventParamName.SEARCH_TERM]: searchTerm,
    });
  };

  useEffect(() => {
    if (isMobile && selectedTab === StockPanelTabs.VIDEOS) {
      getHasUserSeenMobileVideoPreview()
        .then((hasSeen) => {
          if (!hasSeen) {
            dispatch(
              updatePanelData({
                panelId,
                data: {
                  [PanelDataKeys.GROWL]: {
                    icon: 'icon-tap-and-hold',
                    text: window.i18next.t('pmwjs_video_preview_message_growl'),
                  } as PanelGrowlData,
                },
              })
            );
            setTimeout(() => {
              dispatch(
                updatePanelData({
                  panelId,
                  data: {
                    [PanelDataKeys.GROWL]: undefined,
                  },
                })
              );
              void markUserHasSeenMobileVideoPreview();
            }, MOBILE_VIDEO_PREVIEW_GROWL_VISIBLE_DURATION);
          }
        })
        .catch((e: unknown) => {
          console.error(e);
        });
    }
  }, [selectedTab]);

  useEffect(() => {
    const initialOffsets = getInitialOffsetsForInitialCarouselRequest(tabsToShow);
    for (const key in initialOffsets) {
      if (props.blacklistedSources?.includes(key as StockSources)) {
        delete initialOffsets[key as StockSources];
      }
    }
    getStockForCarousel({
      stockSrcToOffsetMap: initialOffsets,
    })
      .then(onCarouselStockReceived)
      .catch((e) => {
        console.error(e);
        setErrorState(TextToImageGridErrorState.GENERIC);
      });
  }, [retryCarousels]);

  useEffect(() => {
    dispatch(setStockGetterOptions(getOptionsForStockGetter(selectedFilters, selectedTab, audioFilterIDs.current)));
  }, [selectedFilters, selectedTab, audioFilterIDs.current]);

  useEffect(() => {
    trackMediaItemSearch();
    toggleFiltersVisibilityIfChanged(selectedTab);
  }, [selectedTab, searchTerm]);

  useEffect(() => {
    if (selectedTab === StockPanelTabs.STICKERS && selectedFilters.length) {
      trackMediaItemSearch(true);
    }
  }, [selectedFilters]);

  useEffect(() => {
    trackPosterBuilderGA4Events(GA4EventName.MEDIA_MODAL_OPEN);
    dispatch(selectTab(tabId, props.defaultSearchTab ?? tabsToShow[0]));
    loadAudioCategories();
    loadRecentStockItems();
    if (showTextToImageTool) {
      loadGeneratedImages();
      getRemainingTextToImageInvocations();
    }

    return () => {
      onModalUnmount();
    };
  }, [panelId]);

  return <Panel key={panelId} panelId={panelId} panelHeader={getHeader()} panelContent={getContent()} panelFooter={getFooter()} />;
}
